// Data hooks for the DefectsImageGallery module: the related-inspections query
// and the defect-image mutations. Extracted from DefectsImageGallery.tsx.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Config from 'config/Config';
import Helper from 'helper/Helper';
import { getInspection } from 'services/api/inspection/queries';
import { fetcher } from 'services/fetcher';

import { UpdateImageParams, UpdateImagePayload } from './defectImageTypes';

export const useRelatedInspections = ({
  currentInspectionId = '',
}: {
  currentInspectionId: string;
}) =>
  useQuery({
    queryKey: ['inspection', currentInspectionId, 'related-cloned-inspections'],

    queryFn: async () => {
      const inspection = await getInspection({
        params: { inspectionId: currentInspectionId },
      });
      const inspectionData = inspection;

      if (!inspectionData.previousInspectionId) {
        return {
          currentInspection: inspectionData,
          previousInspection: null,
        };
      }

      const previousInspection = await getInspection({
        params: { inspectionId: inspectionData.previousInspectionId },
      });
      return {
        currentInspection: inspectionData,
        previousInspection: previousInspection,
      };
    },

    staleTime: Infinity,
  });

const deleteDefectImage = ({
  inspectionId,
  observationId,
  defectId,
  photoId,
}: UpdateImageParams) => {
  const url = Helper.parse(
    Config.DELETE_IMAGE_API,
    inspectionId,
    observationId,
    defectId,
    photoId,
  );

  return fetcher(url, { method: 'DELETE' });
};

const updateImage = (args: UpdateImageParams & UpdateImagePayload) => {
  const { inspectionId, observationId, defectId, photoId, drawables } = args;
  const url = Helper.parse(
    Config.DELETE_IMAGE_API,
    inspectionId,
    observationId,
    defectId,
    photoId,
  );

  return fetcher(url, {
    method: 'PUT',
    body: JSON.stringify({ drawables: drawables }),
  });
};

export const useDeleteDefectPhotoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteDefectImage,
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: ['inspection', variables.inspectionId],
      }),
  });
};

export const useUpdateImage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateImage,
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: ['inspection', variables.inspectionId],
      }),
  });
};
